import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cadeScopeLabel } from '@/pages/cade/conselho/scope';
import type { ConselhoContestedItem } from '@/services/types';

/**
 * CADE's roll call spells the side WINNING/LOSING where ANATEL's spells
 * MAJORITY/MINORITY; reading only the latter left the CADE member page's side
 * column blank on every row.
 */
const MINORITY_SIDES = new Set(['MINORITY', 'LOSING']);
const MAJORITY_SIDES = new Set(['MAJORITY', 'WINNING']);

/**
 * CADE's item key is `<process>|<ata file>|<item>`; only the item number is
 * for a reader. ANATEL's label is already the alinea.
 */
function itemNumber(label: string): string {
  return label.includes('|') ? label.slice(label.lastIndexOf('|') + 1) : label;
}

/** One row per contested (acórdão, alínea); `side` is filled only on a member's own page. */
export function ContestedItemsTable({
  items,
  showSide = false,
  ns = 'anatel',
  processBase = '/anatel/p',
  relatorBase = '/anatel/r',
}: {
  items: ConselhoContestedItem[];
  showSide?: boolean;
  ns?: string;
  processBase?: string;
  relatorBase?: string;
}) {
  const { t, i18n } = useTranslation([ns]);
  const number = new Intl.NumberFormat(i18n.language);
  // CADE carries the legal regime in `direction` and the case family in
  // `tematica` (both ETL codes): shown as one "case type (law)" label, and the
  // direction column, which CADE does not have, is not drawn.
  const isCade = ns === 'cade';
  const subject = (item: ConselhoContestedItem) =>
    isCade && item.tematica
      ? cadeScopeLabel(item.direction ? `${item.direction}:${item.tematica}` : item.tematica, t)
      : (item.tematica ?? '—');
  return (
    <div className="max-h-[32rem] overflow-auto">
      <table className="w-full text-xs" data-testid="contested-items">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t(`${ns}:conselho.items.process`)}</th>
            <th className="p-2">{t(`${ns}:conselho.items.date`)}</th>
            <th className="p-2">{t(`${ns}:conselho.items.subject`)}</th>
            <th className="p-2">{t(`${ns}:conselho.items.relator`)}</th>
            {isCade ? null : <th className="p-2">{t(`${ns}:conselho.items.direction`)}</th>}
            <th className="p-2 text-right">{t(`${ns}:conselho.items.sitting`)}</th>
            <th className="p-2 text-right">{t(`${ns}:conselho.items.minority`)}</th>
            {showSide ? <th className="p-2">{t(`${ns}:conselho.items.side`)}</th> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={`${item.process_key}-${item.item_label}-${index}`} className="border-t">
              <td className="p-2 tabular-nums">
                <Link to={`${processBase}/${item.process_key}`} className="text-primary underline-offset-4 hover:underline">
                  {item.process_number}
                </Link>
                {item.item_label !== 'ALL' ? (
                  <span className="ml-1 text-muted-foreground">{itemNumber(item.item_label)})</span>
                ) : null}
              </td>
              <td className="p-2 tabular-nums text-muted-foreground">{item.session_date ?? '—'}</td>
              <td className="p-2 text-muted-foreground">{subject(item)}</td>
              <td className="p-2">
                {item.relator && item.relator_slug ? (
                  <Link to={`${relatorBase}/${item.relator_slug}`} className="underline-offset-4 hover:underline">
                    {item.relator}
                  </Link>
                ) : (
                  '—'
                )}
              </td>
              {isCade ? null : <td className="p-2 text-muted-foreground">{item.direction ?? '—'}</td>}
              <td className="p-2 text-right tabular-nums">{number.format(item.n_sitting)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(item.n_minority)}</td>
              {showSide ? (
                <td className="p-2" data-side={item.side ?? ''}>
                  {item.side && MINORITY_SIDES.has(item.side)
                    ? t(`${ns}:conselho.items.minoritySide`)
                    : item.side && MAJORITY_SIDES.has(item.side)
                      ? t(`${ns}:conselho.items.majority`)
                      : '—'}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
